import { ReactNode, useRef } from "react";
import { Tooltip as AriaTooltip, OverlayArrow, TooltipTriggerStateContext, TooltipContext } from "react-aria-components";
import { useTooltipTrigger } from "react-aria";
import { useTooltipTriggerState } from "react-stately";
import clsx from "clsx";

export type TooltipPosition = "top" | "bottom" | "left" | "right" | "top-left" | "top-right" | "bottom-left" | "bottom-right";

interface Shortcut {
	key: string;
}

interface TooltipProps {
	children: ReactNode;
	label: ReactNode;
	shortcuts?: Shortcut[];
	variant?: "light" | "dark";
	position?: TooltipPosition;
	delay?: number;
	disabled?: boolean;
	id?: string;
	offset?: number;
	triggerClassName?: string;
	tooltipClassName?: string;
	multiline?: boolean;
	/** When false, hover-only trigger (no role="button" / tab stop). Use inside links/buttons. */
	interactive?: boolean;
}

const placementMap: Record<TooltipPosition, string> = {
	top: "top",
	bottom: "bottom",
	left: "left",
	right: "right",
	"top-left": "top start",
	"top-right": "top end",
	"bottom-left": "bottom start",
	"bottom-right": "bottom end",
};

const baseSide = (p: TooltipPosition) => (p.startsWith("top") ? "top" : p.startsWith("bottom") ? "bottom" : p.startsWith("left") ? "left" : "right");

export function Tooltip({
	children,
	label,
	shortcuts,
	variant = "light",
	position = "top",
	delay = 100,
	disabled = false,
	id,
	offset,
	triggerClassName,
	tooltipClassName,
	multiline = false,
	interactive = true,
}: TooltipProps) {
	const state = useTooltipTriggerState({ delay, isDisabled: disabled });
	const triggerRef = useRef<HTMLElement>(null);

	const { triggerProps, tooltipProps } = useTooltipTrigger({ isDisabled: disabled }, state, triggerRef);

	const scheme =
		variant === "dark"
			? {
					bubble: "bg-navy-900 text-white",
					kbd: "border-navy-500 bg-navy-700 text-white",
					arrow: "fill-navy-900",
					shadow: "",
				}
			: {
					bubble: "bg-white text-gray-900",
					kbd: "border-gray-300 bg-gray-100 text-gray-700",
					arrow: "fill-white",
					shadow: "shadow-[0px_0px_2px_0px_rgba(0,0,0,0.40)]",
				};

	const rotation = {
		top: "",
		bottom: "rotate-180",
		left: "-rotate-90",
		right: "rotate-90",
	}[baseSide(position)];

	const interactiveTriggerProps = interactive
		? {
				tabIndex: 0 as const,
				role: "button" as const,
				"aria-roledescription": "tooltip trigger",
			}
		: {
				// Clear any focusability react-aria may set via triggerProps.
				tabIndex: undefined,
				role: undefined,
			};

	return (
		<TooltipTriggerStateContext.Provider value={state}>
			<TooltipContext.Provider value={{ ...tooltipProps, triggerRef } as any}>
				<span
					ref={triggerRef as any}
					key={id}
					className={clsx("relative inline-flex", triggerClassName)}
					{...triggerProps}
					{...interactiveTriggerProps}
				>
					{children}
				</span>

				<AriaTooltip
					placement={placementMap[position] as any}
					offset={offset}
					className={clsx(
						"select-none rounded-lg px-[10px] py-[6px] text-sm",
						multiline ? "whitespace-normal" : "whitespace-nowrap",
						scheme.bubble,
						scheme.shadow,
						tooltipClassName
					)}
				>
					<OverlayArrow>
						<svg width={8} height={8} viewBox="0 0 8 8" className={clsx(scheme.arrow, rotation)}>
							<path d="M0 0 L4 4 L8 0" />
						</svg>
					</OverlayArrow>
					<div className={clsx("flex gap-1", multiline ? "items-start" : "items-center")}>
						<span>{label}</span>
						{shortcuts?.map(({ key }, i) => (
							<kbd key={i} className={clsx("px-1.5 py-0.5 rounded border text-xs font-mono font-medium", scheme.kbd)}>
								{key}
							</kbd>
						))}
					</div>
				</AriaTooltip>
			</TooltipContext.Provider>
		</TooltipTriggerStateContext.Provider>
	);
}
